import { streamLlmQuery } from "@hooks/utils/llmApi";
import {
  explainerForDirective,
  matchCurrentLocation,
  pathForDirective
} from "@utils/navigationCatalog";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type {
  AssistantMessage,
  AssistantStreamEvent,
  AssistantToolResult
} from "catchment-common/interfaces/llmQuery";
import { v4 as uuid } from "uuid";

import { toContextUsage, type ContextUsage } from "@utils/contextUsage";
import { useTypewriter } from "./useTypewriter";
import {
  CHAT_HISTORY_FRESHNESS_MS,
  chatHistoryDb,
  chatHistoryKey,
  type WorkflowTranscriptMessage
} from "@utils/persistence/chatHistoryDb";

export type { ContextUsage } from "@utils/contextUsage";
export type { WorkflowTranscriptMessage } from "@utils/persistence/chatHistoryDb";

// A write tool asked for explicit confirmation. The drawer renders `action`
// as a Confirm/Cancel card; confirming echoes `token` back to the backend.
export type PendingConfirmation = {
  token: string;
  action: { tool: string; title: string; body: string };
};

export type UseIntentKitAgentChatResult = {
  cancelAction: () => void;
  composerDisabled: boolean;
  confirmAction: () => void;
  contextUsage?: ContextUsage;
  isAwaitingIntent: boolean;
  isProcessing: boolean;
  messages: WorkflowTranscriptMessage[];
  pendingConfirmation?: PendingConfirmation;
  reset: () => void;
  sendUserMessage: (
    text: string,
    options?: { confirmedTokens?: string[] }
  ) => Promise<void>;
  sessionKey: number;
  statusLine: string;
  stopGenerating: () => void;
};

// ─── Pure helpers ─────────────────────────────────────────────────────────

const greeting = (): WorkflowTranscriptMessage => ({
  createdAt: Date.now(),
  id: uuid(),
  role: "assistant",
  text: "How can I help?"
});

const TOOL_START_LABELS: Record<string, string> = {
  grantProjectAccess: "Updating project access...",
  listProjectsWithAccess: "Checking project access...",
  manageOrgUser: "Working with users...",
  navigateTo: "Taking you there...",
  webSearch: "Searching the web..."
};

const toolStartLabel = (name: string): string =>
  TOOL_START_LABELS[name] ?? "Working on it...";

const toolEndLabel = (name: string, result: AssistantToolResult): string => {
  if (result.status === "success") return "";
  if (result.status === "needs_input") return "Needs a bit more detail...";
  if (result.status === "ambiguous") return "Found multiple matches...";
  return `${toolStartLabel(name).replace(/\.\.\.$/, "")} didn't complete.`;
};

const isOrgUserCreate = (name: string, result: AssistantToolResult): boolean =>
  name === "manageOrgUser" &&
  result.status === "success" &&
  (result.data as { action?: string } | undefined)?.action === "create";

const sanitizeError = (e: unknown): string => {
  const msg =
    e instanceof Error
      ? e.message
      : typeof e === "string"
        ? e
        : "Something went wrong.";
  return /timed?\s*out|timeout|took too long/i.test(msg)
    ? "This request took too long. Please try again."
    : msg;
};

// ─── Hook ─────────────────────────────────────────────────────────────────

// Tight enough that a poisoned earlier turn (a hallucinated route
// confirmation, a stale resolution) cannot survive more than a handful of
// follow-ups. The backend already runs sanitizeMessageHistory + pre-flight
// compaction, so a longer window buys little correctness and a lot of risk.
const MAX_CONVERSATION = 12;
const MAX_TRANSCRIPT = 40;

const trimHistory = (
  conversation: AssistantMessage[],
  transcript: WorkflowTranscriptMessage[]
): {
  conversation: AssistantMessage[];
  transcript: WorkflowTranscriptMessage[];
} => ({
  conversation: conversation.slice(-MAX_CONVERSATION),
  transcript: transcript.slice(-MAX_TRANSCRIPT)
});

export const useIntentKitAgentChat = (args: {
  onUserCreated?: () => void;
  orgId: string;
  userId: string;
}): UseIntentKitAgentChatResult => {
  const { orgId, userId } = args;
  const storageKey = chatHistoryKey(orgId, userId);

  const [sessionKey, setSessionKey] = useState(0);
  const [messages, setMessages] = useState<WorkflowTranscriptMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [statusLine, setStatusLine] = useState("");
  const [contextUsage, setContextUsage] = useState<ContextUsage>();
  const [pendingConfirmation, setPendingConfirmation] =
    useState<PendingConfirmation>();

  // Server-side conversation history (full ModelMessage[] round-tripped via
  // the `done` event). Kept separately from the UI transcript above.
  const conversation = useRef<AssistantMessage[]>([]);
  // Stable per-conversation id sent as the request `sessionId` and used by the backend as the Laminar sessionId.
  const conversationIdRef = useRef<string>(uuid());
  const transcriptRef = useRef<WorkflowTranscriptMessage[]>([]);
  transcriptRef.current = messages;
  const storageKeyRef = useRef(storageKey);
  storageKeyRef.current = storageKey;

  const persist = useCallback(
    (
      nextConversation: AssistantMessage[],
      nextTranscript: WorkflowTranscriptMessage[],
      nextUsage?: ContextUsage
    ): void => {
      const trimmed = trimHistory(nextConversation, nextTranscript);
      void chatHistoryDb.chatHistory.put({
        contextUsage: nextUsage,
        conversation: trimmed.conversation,
        conversationId: conversationIdRef.current,
        key: storageKeyRef.current,
        transcript: trimmed.transcript,
        updatedAt: Date.now()
      });
    },
    []
  );
  const activeRequestId = useRef<string>();
  const abortController = useRef<AbortController>();
  const onUserCreatedRef = useRef(args.onUserCreated);
  onUserCreatedRef.current = args.onUserCreated;

  // Current route, kept in refs so sendUserMessage reads the latest without
  // being re-created on every navigation. Used to tell the agent where the
  // user is, and to perform the navigation the agent asks for.
  const routerLocation = useLocation();
  const navigate = useNavigate();
  // Include the search string so the agent knows query-based context (e.g. the
  // current Settings tab) and so navigation dedup compares against the full
  // target URL that pathForDirective produces.
  const pathnameRef = useRef(routerLocation.pathname + routerLocation.search);
  pathnameRef.current = routerLocation.pathname + routerLocation.search;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const updateAssistantText = useCallback((id: string, text: string) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, text } : m)));
  }, []);

  const typewriter = useTypewriter(updateAssistantText);

  // Hydrate from IndexedDB whenever the (orgId, userId) key changes — including
  // initial mount. If no row exists we seed with the greeting only; the
  // greeting is a UI artifact and is not persisted on its own.
  useEffect(() => {
    let cancelled = false;
    void chatHistoryDb.chatHistory.get(storageKey).then((row) => {
      if (cancelled) return;
      const isFresh =
        row && Date.now() - (row.updatedAt ?? 0) < CHAT_HISTORY_FRESHNESS_MS;
      if (row && row.transcript.length > 0 && isFresh) {
        conversation.current = row.conversation;
        // Continue the same Laminar session across reloads. Older rows predate
        // this field — fall back to a fresh id so they still group going forward.
        conversationIdRef.current = row.conversationId ?? uuid();
        setMessages(row.transcript);
        setContextUsage(row.contextUsage);
      } else {
        // Stale or missing: drop the row so a refresh doesn't carry the
        // previous session forward, then start clean.
        if (row && !isFresh) {
          void chatHistoryDb.chatHistory.delete(storageKey);
        }
        conversation.current = [];
        conversationIdRef.current = uuid();
        setMessages([greeting()]);
        setContextUsage(undefined);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [storageKey]);

  const reset = useCallback(() => {
    abortController.current?.abort();
    typewriter.cancelAll();
    abortController.current = undefined;
    activeRequestId.current = undefined;
    conversation.current = [];
    // New chat ⇒ new session.
    conversationIdRef.current = uuid();
    setContextUsage(undefined);
    setIsLoading(false);
    setMessages([greeting()]);
    setPendingConfirmation(undefined);
    setSessionKey((k) => k + 1);
    setStatusLine("");
    void chatHistoryDb.chatHistory.delete(storageKeyRef.current);
  }, [typewriter]);

  const stopGenerating = useCallback(() => {
    abortController.current?.abort();
    typewriter.cancelAll();
    abortController.current = undefined;
    activeRequestId.current = undefined;
    setIsLoading(false);
    setPendingConfirmation(undefined);
    setStatusLine("");
  }, [typewriter]);

  const sendUserMessage = useCallback(
    async (text: string, options?: { confirmedTokens?: string[] }) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading) return;

      // Any new message dismisses a pending confirmation card; confirm/cancel
      // clear it themselves before calling here.
      setPendingConfirmation(undefined);

      const requestId = uuid();
      const assistantId = uuid();
      const controller = new AbortController();
      activeRequestId.current = requestId;
      abortController.current = controller;

      const requestMessages: AssistantMessage[] = [
        ...conversation.current,
        { content: trimmed, role: "user" }
      ];

      const userMessage: WorkflowTranscriptMessage = {
        createdAt: Date.now(),
        id: uuid(),
        role: "user",
        text: trimmed
      };
      const assistantPlaceholder: WorkflowTranscriptMessage = {
        createdAt: Date.now(),
        id: assistantId,
        role: "assistant",
        text: ""
      };
      let nextTranscript: WorkflowTranscriptMessage[] = [];
      setMessages((prev) => {
        nextTranscript = [...prev, userMessage, assistantPlaceholder];
        return nextTranscript;
      });
      setIsLoading(true);
      setStatusLine("Thinking...");

      // Persist the user turn immediately so a refresh mid-stream doesn't
      // lose the question. The empty assistant placeholder is filtered out.
      persist(
        requestMessages,
        nextTranscript.filter((m) => !(m.id === assistantId && m.text === "")),
        contextUsage
      );

      // Live request scratch: streamed text + flags collected from events.
      // Pulled out of the event loop so the loop reads as a switch.
      let streamed = "";
      let createdUser = false;
      let latestUsage: ContextUsage | undefined = contextUsage;
      // When navigation fires we show the route's authored userExplainer
      // immediately (deterministic copy, no LLM lag) as the landing text, then
      // let the agent APPEND its own tailored guidance after it — e.g. answering
      // a "how do I do X here?" question. `pendingNavSeparator` makes the first
      // text burst after the explainer start on its own paragraph.
      let pendingNavSeparator = false;
      // Fallback text if the model ends a turn silently after a tool call (it
      // occasionally does this — most commonly when it hits the step cap mid-
      // chain). We prefer the most recent successful workflow's summarize
      // output; if no success ran this turn we fall back to any tool message
      // (e.g. an ambiguous list of candidates) so the user still sees what
      // happened.
      let lastSuccessMessage = "";
      let lastToolMessage = "";
      // The agent can emit text, then call a tool, then emit MORE text in the
      // same turn (e.g. "tried to update… let me retry" → findOrgUser → "the
      // user is already a standard user"). Without a separator the two bursts
      // glue into one paragraph and read like one contradictory thought. We
      // append the separator at the END of the previous burst (on tool_start)
      // rather than prepending it to the next text delta — otherwise the model
      // emitting its own leading "\n" stacks with ours and we get a visible
      // blank line at the start of every follow-up segment.

      const isStale = () => activeRequestId.current !== requestId;

      const handleEvent = (event: AssistantStreamEvent): void => {
        switch (event.type) {
          case "text":
            // After a navigation the canonical explainer is already on screen;
            // separate the agent's tailored follow-up so it reads as its own
            // paragraph instead of gluing onto the explainer.
            if (pendingNavSeparator) {
              if (streamed && !streamed.endsWith("\n\n")) {
                streamed = streamed.replace(/\s+$/, "") + "\n\n";
              }
              pendingNavSeparator = false;
            }
            streamed += event.delta;
            setStatusLine("");
            typewriter.setTarget(assistantId, streamed);
            return;
          case "tool_start":
            // Close the prior text burst with a paragraph break so any
            // follow-up text after this tool stays visually separated. Only
            // when there is prior text and it doesn't already end with one.
            if (streamed && !streamed.endsWith("\n\n")) {
              streamed = streamed.replace(/\s+$/, "") + "\n\n";
              typewriter.setTarget(assistantId, streamed);
            }
            setStatusLine(toolStartLabel(event.name));
            return;
          case "tool_end":
            setStatusLine(toolEndLabel(event.name, event.result));
            if (event.result.status === "pending_confirmation") {
              // The action resolved but needs a real Confirm click before it
              // runs. Surface the card; the agent's own text ("…confirm?")
              // streams alongside it.
              setPendingConfirmation({
                action: event.result.action,
                token: event.result.confirmationToken
              });
            }
            if (isOrgUserCreate(event.name, event.result)) createdUser = true;
            if ("message" in event.result && event.result.message) {
              lastToolMessage = event.result.message;
              if (event.result.status === "success") {
                lastSuccessMessage = event.result.message;
              }
            }
            return;
          case "navigate": {
            const target = pathForDirective(event.directive);
            if (target && target !== pathnameRef.current) {
              navigateRef.current(target);
            }
            // Show the route's authored user-facing explainer immediately:
            // canonical, page-correct landing copy with zero LLM lag. We APPEND
            // it (rather than replace) so any text the agent emitted before
            // navigating is preserved, and mark a pending separator so the
            // agent's follow-up guidance (e.g. answering "how do I do X here?")
            // streams in as its own paragraph after it. For a bare "go to X" the
            // agent stays silent, so the explainer alone stands.
            const explainer = explainerForDirective(event.directive);
            if (streamed && !streamed.endsWith("\n\n")) {
              streamed = streamed.replace(/\s+$/, "") + "\n\n";
            }
            streamed += explainer;
            pendingNavSeparator = true;
            setStatusLine("");
            typewriter.setTarget(assistantId, streamed);
            return;
          }
          case "done":
            conversation.current = event.messages;
            latestUsage = toContextUsage(event.usage);
            setContextUsage(latestUsage);
            return;
          case "error":
            throw new Error(event.message);
        }
      };

      try {
        for await (const event of streamLlmQuery(
          {
            confirmedTokens: options?.confirmedTokens,
            location: matchCurrentLocation(pathnameRef.current),
            messages: requestMessages,
            sessionId: conversationIdRef.current
          },
          controller.signal
        )) {
          if (isStale()) return;
          handleEvent(event);
        }

        if (isStale()) return;
        if (!streamed.trim()) {
          // Never throw at the user. Prefer the most recent success message,
          // fall back to any tool message (ambiguous, needs_input), and
          // finally a generic nudge so the chat doesn't dead-end.
          streamed =
            lastSuccessMessage ||
            lastToolMessage ||
            "I wasn't able to finish that one. Could you rephrase or try again?";
        }
        if (createdUser) onUserCreatedRef.current?.();

        setStatusLine("");
        typewriter.setTarget(assistantId, streamed);

        // Persist the finalised turn. Replace the streaming placeholder with
        // the full assistant text so a reload shows the message in full,
        // even if the typewriter hasn't finished animating yet.
        const finalisedTranscript = transcriptRef.current.map((m) =>
          m.id === assistantId ? { ...m, text: streamed } : m
        );
        persist(conversation.current, finalisedTranscript, latestUsage);
      } catch (error) {
        if (
          isStale() ||
          (error instanceof DOMException && error.name === "AbortError")
        ) {
          return;
        }
        typewriter.reset(assistantId);
        setStatusLine("");
        const errorText = `Could not complete the request: ${sanitizeError(error)}`;
        updateAssistantText(assistantId, errorText);

        // Persist the failed turn so the error bubble survives a reload. The
        // user message was already persisted before the request (above); without
        // this the assistant's response is lost and a reload shows a dangling
        // user turn with no reply. conversation.current holds the last good
        // history (the failed turn is not appended to it), so it stays replayable.
        const finalisedTranscript = transcriptRef.current.map((m) =>
          m.id === assistantId ? { ...m, text: errorText } : m
        );
        persist(conversation.current, finalisedTranscript, latestUsage);
      } finally {
        if (!isStale()) {
          activeRequestId.current = undefined;
          abortController.current = undefined;
          setIsLoading(false);
        }
      }
    },
    [contextUsage, isLoading, persist, typewriter, updateAssistantText]
  );

  // The user clicked Confirm (or pressed Enter): echo the token back so the
  // gated write runs exactly once. The token is sent ONLY on this turn — it is
  // never written into `conversation.current`, so it can't be replayed.
  const confirmAction = useCallback(() => {
    const pending = pendingConfirmation;
    if (!pending) return;
    setPendingConfirmation(undefined);
    void sendUserMessage("Yes, go ahead.", {
      confirmedTokens: [pending.token]
    });
  }, [pendingConfirmation, sendUserMessage]);

  // Cancel: no token, so the action stays un-run; the agent just acknowledges.
  const cancelAction = useCallback(() => {
    if (!pendingConfirmation) return;
    setPendingConfirmation(undefined);
    void sendUserMessage("No, cancel that.");
  }, [pendingConfirmation, sendUserMessage]);

  return {
    cancelAction,
    composerDisabled: isLoading,
    confirmAction,
    contextUsage,
    isAwaitingIntent: messages.every((m) => m.role !== "user"),
    isProcessing: isLoading,
    messages,
    pendingConfirmation,
    reset,
    sendUserMessage,
    sessionKey,
    statusLine,
    stopGenerating
  };
};
